import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Phone } from 'lucide-react';
import { clinicInfo } from '../data/mock';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Empty dependency array is correct here since handleScroll doesn't use external state

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/doctors', label: 'Doctors' },
    { path: '/symptoms', label: 'Symptoms' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_care-staging/artifacts/sinmgq9e_hw_logo.png" 
              alt="Healthwrx" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-[#14D1A2] ${
                  location.pathname === link.path ? 'text-[#14D1A2]' : 'text-gray-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="https://www.averrun.co"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-gray-700 transition-colors hover:text-[#14D1A2]"
            >
              Preventive & Long-Term Care
            </a>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <a href={`tel:${clinicInfo.phone}`} className="flex items-center space-x-2 text-gray-700 hover:text-[#14D1A2] transition-colors">
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">{clinicInfo.phone}</span>
            </a>
            <Link to="/book-appointment">
              <Button className="bg-[#14D1A2] hover:bg-[#10B88A] text-white">
                Book Appointment
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-gray-700"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden pb-6 pt-2 border-t">
            <nav className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-base font-medium transition-colors hover:text-[#14D1A2] ${
                    location.pathname === link.path ? 'text-[#14D1A2]' : 'text-gray-700'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="https://www.averrun.co"
                target="_blank"
                rel="noopener noreferrer"
                className="text-base font-medium text-gray-700 transition-colors hover:text-[#14D1A2]"
              >
                Preventive & Long-Term Care
              </a>
              <div className="pt-4 space-y-3">
                <a href={`tel:${clinicInfo.phone}`} className="flex items-center space-x-2 text-gray-700">
                  <Phone className="w-4 h-4" />
                  <span className="text-sm font-medium">{clinicInfo.phone}</span>
                </a>
                <Link to="/book-appointment" onClick={() => setIsMenuOpen(false)}>
                  <Button className="w-full bg-[#14D1A2] hover:bg-[#10B88A] text-white">
                    Book Appointment
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
